/**
 * The integration section of the Dynamics board (L1): Service Bus queue health.
 *
 * **What the source actually is.** Not Service Bus itself and not any message:
 * Azure Monitor's *platform metrics* for one namespace, landed in the
 * `AzureMetrics` table of a **second Log Analytics workspace, in a different
 * subscription** from every other panel here. Two consequences the copy has to
 * carry. Backlog figures are 5-minute means, so they describe a shape over the
 * window rather than a count at any instant. And the table is populated only by
 * a diagnostic setting on the namespace — no setting, no rows, whatever the
 * queues are doing.
 *
 * Because it is a different workspace it carries its own `access_state` and is
 * never gated on the others'. As of 2026-08-20 the expected state is
 * `workspace_not_found`: the workspace resource ID inherited from the Power BI
 * model does not resolve, and `workspace_resource_id` is echoed back precisely
 * so an operator can compare what was tried against what exists. That renders as
 * the access panel — never as an empty backlog chart, and never as a
 * zero-message queue, which would read as a perfectly drained integration layer.
 * The full list of what has to be configured lives one click deeper, on
 * `/dynamics/queues`, so this section points there rather than repeating it.
 *
 * Discovery, not a hard-coded list (§8): `queues_requested` is empty by default,
 * which means "every queue the namespace produced metrics for". A newly
 * provisioned queue therefore appears on this board with no deploy. When it is
 * non-empty, the caption says so, because a scoped panel is a lower bound.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { FoIntegration, FoQueueHealth, FoQueuePoint } from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";
import { DrillLink } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { DYNAMICS_QUEUES_PATH, INTEGRATION_ANCHOR } from "./params";
import {
  formatBinTime,
  formatCount,
  formatMessageCount,
  formatWindow,
  HEALTH_COLOR,
  HEALTH_ICON,
  HEALTH_LABEL,
  HEALTH_ORDER,
  HEALTH_TONE,
  tooltipValue,
} from "./status";

/** How many queues the L1 chart ranks — the rest live at L2. */
const WORST_LIMIT = 5;

/** A trend row: one 5-minute bin, one numeric column per queue present. */
type QueueTrendRow = { bucket: string; label: string } & Record<string, number | string>;

interface IntegrationSectionProps {
  integration: FoIntegration;
}

export function IntegrationSection({ integration }: IntegrationSectionProps) {
  const windowLabel = formatWindow(integration.window);
  const staleAfterMs = integration.cache_ttl_sec * 2000;

  // Colours assigned from the FULL queue list, sorted by name, so a queue keeps
  // its colour whatever the trend contains in this window (§3.3).
  const queueNames = [...new Set(integration.queues.map((row) => row.queue))].sort((a, b) =>
    a.localeCompare(b),
  );
  const trendConfig = identityConfig(queueNames);

  // A bin with no reported backlog for a queue leaves the key absent, which
  // draws a gap. Service Bus not reporting is not the same as an empty queue.
  const byBucket = new Map<string, Record<string, number>>();
  for (const point of integration.trend) {
    if (point.active_messages == null) {
      continue;
    }
    const row = byBucket.get(point.bucket) ?? {};
    row[point.queue] = point.active_messages;
    byBucket.set(point.bucket, row);
  }
  const trendRows: QueueTrendRow[] = [...byBucket.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, series]) => ({ bucket, label: formatBinTime(bucket), ...series }));

  // Only queues that reported a peak backlog can be ranked by it. A queue with
  // no reported metric is not a queue with nothing in it.
  const reported = integration.queues.filter((row) => row.peak_active_messages != null);
  const unreported = integration.queues.filter((row) => row.peak_active_messages == null);
  const backlogRows = [...reported].sort(
    (a, b) =>
      (b.peak_active_messages ?? 0) - (a.peak_active_messages ?? 0) ||
      a.queue.localeCompare(b.queue),
  );
  const worstQueues = backlogRows.slice(0, WORST_LIMIT);

  const verdictCounts = HEALTH_ORDER.map((state) => ({
    state,
    count: integration.queues.filter((row) => row.state === state).length,
  })).filter((entry) => entry.count > 0);

  const scopeCaption =
    integration.queues_requested.length > 0
      ? `scoped to ${integration.queues_requested.length} configured queues — a newly provisioned queue will not appear until the scope is widened`
      : "every queue the namespace reported, so a new queue appears without a deploy";

  return (
    <section id={INTEGRATION_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      {/* Names the source before the first chart: these are platform metrics
          about a namespace, from a workspace nothing else on this board reads. */}
      <Typography variant="body-sm" tone="muted">
        Azure Monitor platform metrics for the Service Bus namespace{" "}
        <code className="text-fg">{integration.namespace}</code>, read from the{" "}
        {integration.workspace} workspace — a different one from every other panel on this board,
        and one that holds these metrics only while a diagnostic setting on the namespace exports
        them. Backlog figures are 5-minute means, so they describe a shape over the window rather
        than a message count at any instant.
      </Typography>

      <ChartCard<FoQueueHealth>
        title="Peak queue backlog"
        caption={`${integration.namespace} · ${windowLabel} · ${scopeCaption}`}
        since={integration.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-queues",
          ariaLabel: "Every Service Bus queue the namespace reported",
          data: integration.queues,
          getRowId: (row) => row.queue,
          columns: [
            { id: "queue", header: "Queue", accessor: (row) => row.queue },
            {
              id: "state",
              header: "Verdict",
              accessor: (row) => row.state,
              cell: (row) => (
                <Badge tone={HEALTH_TONE[row.state]} variant="subtle" size="xs">
                  {HEALTH_LABEL[row.state]}
                </Badge>
              ),
            },
            {
              id: "peak",
              header: "Peak backlog",
              align: "end",
              accessor: (row) => row.peak_active_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatMessageCount(row.peak_active_messages)}</span>
              ),
            },
            {
              id: "active",
              header: "Mean backlog",
              align: "end",
              accessor: (row) => row.active_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatMessageCount(row.active_messages)}</span>
              ),
            },
            {
              id: "in",
              header: "Enqueued",
              align: "end",
              accessor: (row) => row.incoming_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.incoming_messages)}</span>
              ),
            },
            {
              id: "out",
              header: "Dequeued",
              align: "end",
              accessor: (row) => row.outgoing_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.outgoing_messages)}</span>
              ),
            },
            {
              id: "dead",
              header: "Dead-lettered",
              align: "end",
              accessor: (row) => row.dead_lettered_messages,
              cell: (row) => (
                <span className="tabular-nums">
                  {formatMessageCount(row.dead_lettered_messages)}
                </span>
              ),
            },
          ],
        }}
      >
        <div className="flex flex-col gap-3">
          {worstQueues.length === 0 ? (
            <EmptyState
              size="sm"
              title="No queue reported a backlog metric"
              description="No active-message metric arrived for this namespace in the window. The namespace exports platform metrics into the workspace only while a diagnostic setting says so, so this is an absence of measurement, not an empty queue. Queues lists what has to be configured."
            />
          ) : (
            <Chart.Container
              config={{ peak_active_messages: { label: "Peak active messages" } }}
              className="h-64"
              aria-label={`Peak active message backlog per Service Bus queue, ${windowLabel}, worst first`}
            >
              <Chart.BarChart
                data={worstQueues}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="queue" width={180} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: FoQueueHealth) => [
                        { label: "Verdict", value: HEALTH_LABEL[datum.state] },
                        {
                          label: "Peak backlog",
                          value: formatMessageCount(datum.peak_active_messages),
                        },
                        { label: "Mean backlog", value: formatMessageCount(datum.active_messages) },
                        {
                          label: "Enqueued / dequeued",
                          value: `${formatCount(datum.incoming_messages)} / ${formatCount(
                            datum.outgoing_messages,
                          )}`,
                        },
                        {
                          label: "Dead-lettered",
                          value: formatMessageCount(datum.dead_lettered_messages),
                        },
                      ]}
                    />
                  }
                />
                <Chart.ReferenceLine
                  x={integration.warning_backlog}
                  stroke="var(--norican-warning)"
                  strokeDasharray="4 4"
                  label={{
                    value: `degraded above ${formatCount(integration.warning_backlog)}`,
                    position: "insideTopRight",
                    fontSize: 11,
                    fill: "var(--norican-fg-muted)",
                  }}
                />
                <Chart.ReferenceLine
                  x={integration.critical_backlog}
                  stroke="var(--norican-danger)"
                  strokeDasharray="4 4"
                  label={{
                    value: `failing above ${formatCount(integration.critical_backlog)}`,
                    position: "insideBottomRight",
                    fontSize: 11,
                    fill: "var(--norican-fg-muted)",
                  }}
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="peak_active_messages"
                  radius={[0, 3, 3, 0]}
                >
                  {worstQueues.map((row) => (
                    <Chart.Cell key={row.queue} fill={HEALTH_COLOR[row.state]} />
                  ))}
                </Chart.Bar>
              </Chart.BarChart>
            </Chart.Container>
          )}

          {/* Verdict legend with icons — never colour-alone (§3.2). */}
          {verdictCounts.length > 0 && (
            <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
              {verdictCounts.map(({ state, count }) => (
                <span key={state} className="inline-flex items-center gap-1.5">
                  <Icon icon={HEALTH_ICON[state]} size="xs" />
                  {HEALTH_LABEL[state]} ({count})
                </span>
              ))}
            </div>
          )}

          {unreported.length > 0 && (
            <Typography variant="body-sm" tone="muted">
              Not ranked above, because no backlog metric arrived for them:{" "}
              {unreported.map((row) => row.queue).join(", ")}.
            </Typography>
          )}
        </div>
      </ChartCard>

      <ChartCard<FoQueuePoint>
        title="Active messages over time"
        caption={`5-minute bins, ${windowLabel} · a bin with no reported metric is a gap, not an empty queue`}
        since={integration.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-queue-trend",
          ariaLabel: "Active message backlog per 5-minute bin and queue",
          data: integration.trend,
          getRowId: (row) => `${row.bucket}-${row.queue}`,
          columns: [
            { id: "bucket", header: "Bin", accessor: (row) => formatBinTime(row.bucket) },
            { id: "queue", header: "Queue", accessor: (row) => row.queue },
            {
              id: "active",
              header: "Active",
              align: "end",
              accessor: (row) => row.active_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatMessageCount(row.active_messages)}</span>
              ),
            },
            {
              id: "in",
              header: "Enqueued",
              align: "end",
              accessor: (row) => row.incoming_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.incoming_messages)}</span>
              ),
            },
            {
              id: "out",
              header: "Dequeued",
              align: "end",
              accessor: (row) => row.outgoing_messages,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.outgoing_messages)}</span>
              ),
            },
          ],
        }}
      >
        {trendRows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No backlog trend to draw"
            description="No 5-minute bin carried an active-message figure for any queue in this window. An empty axis is not a flat line at zero."
          />
        ) : (
          <Chart.Container
            config={trendConfig}
            className="h-64"
            aria-label={`Active message backlog per queue in 5-minute bins, ${windowLabel}`}
          >
            <Chart.LineChart data={trendRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis {...Chart.axisProps} width={52} allowDecimals={false} />
              <Chart.Tooltip
                content={
                  // A queue with no bin here has no key, and `Number(undefined)`
                  // is `NaN`. The backlog is also a float mean, so it rounds.
                  <Chart.TooltipContent
                    formatter={(value) => formatMessageCount(tooltipValue(value))}
                  />
                }
              />
              <Chart.ReferenceLine
                y={integration.warning_backlog}
                stroke="var(--norican-warning)"
                strokeDasharray="4 4"
                label={{
                  value: `degraded above ${formatCount(integration.warning_backlog)}`,
                  position: "insideTopRight",
                  fontSize: 11,
                  fill: "var(--norican-fg-muted)",
                }}
              />
              {queueNames.map((queue) => (
                <Chart.Line
                  key={queue}
                  {...Chart.seriesDefaults}
                  dataKey={queue}
                  type="monotone"
                  stroke={trendConfig[queue]?.color}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
              {queueNames.length >= 2 && <Chart.Legend content={<Chart.LegendContent />} />}
            </Chart.LineChart>
          </Chart.Container>
        )}
      </ChartCard>

      {/* Never gated on the queue count, in either direction: this panel's
          caption holds the board's only link to /dynamics/queues, so hiding it
          — below six queues, or below one — left that page, its per-queue
          drawer and its setup panel with no path from the board (§4.1). Zero
          queues is exactly when the setup panel is the thing worth reaching. */}
      <PanelCard
        title="Queues"
        caption={
          integration.queues.length === 0 ? (
            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span>None reported</span>
              <DrillLink
                to={DYNAMICS_QUEUES_PATH}
                className="text-accent text-sm font-medium hover:underline"
              >
                What Queues needs in order to read anything →
              </DrillLink>
            </span>
          ) : integration.queues.length > WORST_LIMIT ? (
            <DrillLink
              to={DYNAMICS_QUEUES_PATH}
              className="text-accent text-sm font-medium hover:underline"
            >
              View all {formatCount(integration.queues.length)} queues →
            </DrillLink>
          ) : (
            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span>{`${formatCount(integration.queues.length)} queues reported`}</span>
              <DrillLink
                to={DYNAMICS_QUEUES_PATH}
                className="text-accent text-sm font-medium hover:underline"
              >
                Open the full queue table →
              </DrillLink>
            </span>
          )
        }
      >
        {integration.queues.length === 0 ? (
          <EmptyState
            size="sm"
            title="The namespace listed no queue at all"
            description="Not one queue name came back from the workspace for this window, so there is nothing to rank. The Queues screen names the three settings that have to be in place before one can."
          />
        ) : worstQueues.length === 0 ? (
          <EmptyState
            size="sm"
            title="No queue can be ranked by backlog"
            description="Every queue the namespace listed is on the full table, but none reported an active-message metric in this window. That is an absence of measurement, not an empty queue."
          />
        ) : (
          <DataGrid<FoQueueHealth>
            data={worstQueues}
            getRowId={(row) => row.queue}
            density="compact"
            aria-label={`The ${WORST_LIMIT} queues with the largest backlog`}
            columns={[
              {
                id: "queue",
                header: "Queue",
                accessor: (row) => row.queue,
                cell: (row) => <span className="text-fg text-sm font-medium">{row.queue}</span>,
              },
              {
                id: "state",
                header: "Verdict",
                accessor: (row) => row.state,
                cell: (row) => (
                  <Badge tone={HEALTH_TONE[row.state]} variant="subtle" size="xs">
                    {HEALTH_LABEL[row.state]}
                  </Badge>
                ),
              },
              {
                id: "peak",
                header: "Peak backlog",
                align: "end",
                accessor: (row) => row.peak_active_messages,
                cell: (row) => (
                  <span className="tabular-nums">
                    {formatMessageCount(row.peak_active_messages)}
                  </span>
                ),
              },
              {
                id: "dead",
                header: "Dead-lettered",
                align: "end",
                accessor: (row) => row.dead_lettered_messages,
                cell: (row) => (
                  <span className="tabular-nums">
                    {formatMessageCount(row.dead_lettered_messages)}
                  </span>
                ),
              },
            ]}
          />
        )}
      </PanelCard>
    </section>
  );
}
